/*
 * Audiocolour BPM to Samples & Loop Calculator — interface.
 * Ordinary deferred script. Requires audiocolour-bpm-samples-core.js to be
 * loaded first (it publishes globalThis.AudiocolourBpmSamplesCore). Every
 * calculated value comes from the core; this file only reads inputs, handles
 * events and formats results. One instance per [data-ac-bpm-samples] root.
 */
(function () {
  "use strict";

  // Guard against the script being included twice (e.g. two sections on a page).
  if (window.AudiocolourBpmSamples) return;

  var COPY_FEEDBACK_MS = 1500;
  var MINUS = "−";
  var DIGITS = { seconds: 6, ms: 3, bpm: 3, percent: 3, semitones: 3 };
  var PANELS = ["loop", "find", "match"];
  var PANEL_NAMES = { loop: "Calculate loop", find: "Find BPM", match: "Match tempo" };
  var VARIANT_LABELS = { straight: "Straight", dotted: "Dotted", triplet: "Triplet" };
  var READING_LABELS = { base: "As entered", "half-time": "Half-time", "double-time": "Double-time" };
  var MESSAGES = {
    bpmEmpty: "Enter a tempo between 20 and 300 BPM.",
    bpmRange: "Tempo must be between 20 and 300 BPM.",
    bpmNumber: "Enter a number, e.g. 120 or 128.5.",
    bars: "Enter a whole number of bars from 1 to 999.",
    seconds: "Enter a duration in seconds greater than 0, e.g. 8 or 7.385.",
    frames: "Enter a whole number of sample frames greater than 0, e.g. 352800."
  };

  var instances = new WeakMap();
  var groupedFormats = {};

  // ---- Formatting (display only; no calculation) ---------------------------

  function grouped(value, digits) {
    if (!groupedFormats[digits]) {
      groupedFormats[digits] = new Intl.NumberFormat("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
    }
    return groupedFormats[digits].format(value);
  }

  // Plain number for copying: no grouping, ASCII minus.
  function plain(value, digits) {
    var text = value.toFixed(digits);
    return Number(text) === 0 ? (0).toFixed(digits) : text;
  }

  // Tempo without trailing zeros, up to three decimals ("120", "128.5").
  function bpmText(bpm) {
    return String(Number(bpm.toFixed(DIGITS.bpm)));
  }

  function signed(value, digits) {
    var magnitude = Math.abs(value).toFixed(digits);
    if (Number(magnitude) === 0) return magnitude;
    return (value < 0 ? MINUS : "+") + magnitude;
  }

  function plural(count, one, many) {
    return grouped(count, 0) + " " + (count === 1 ? one : many);
  }

  function quarterNotesText(count) {
    var text = String(Number(count.toFixed(3)));
    return text + (count === 1 ? " quarter note" : " quarter notes");
  }

  function signatureLabel(numerator, denominator) {
    return numerator + "/" + denominator;
  }

  function createInstance(root, core) {
    var tablist = root.querySelector("[data-ac-bpm-samples-tablist]");
    var status = root.querySelector("[data-ac-bpm-samples-status]");
    var tabs = Array.prototype.slice.call(root.querySelectorAll("[data-ac-bpm-samples-tab]"));
    var panels = {};
    PANELS.forEach(function (name) {
      panels[name] = root.querySelector('[data-ac-bpm-samples-panel="' + name + '"]');
    });
    if (!tablist || !status || tabs.length !== PANELS.length || PANELS.some(function (name) { return !panels[name]; })) return null;

    var notesHead = panels.loop.querySelector("[data-notes-head]");
    var notesBody = panels.loop.querySelector("[data-notes-body]");
    var copyAll = panels.loop.querySelector("[data-copy-all]");
    var copyFeedback = panels.loop.querySelector("[data-copy-feedback]");
    var readingsList = panels.find.querySelector('[data-output-list="interpretations"]');
    if (!notesHead || !notesBody || !copyAll || !copyFeedback || !readingsList) return null;

    var controller = new AbortController();
    var timers = { announce: null, copy: null, feedback: null };
    var values = { loop: {}, find: {}, match: {} }; // validated numeric field values (or null)
    var state = {
      loopExport: null, // latest valid loop + table results, for Copy all
      copySequence: 0,
      copyButton: null
    };

    function on(target, type, handler) {
      target.addEventListener(type, handler, { signal: controller.signal });
    }

    function clearTimer(name) {
      if (timers[name] !== null) {
        window.clearTimeout(timers[name]);
        timers[name] = null;
      }
    }

    function announce(message) {
      clearTimer("announce");
      status.textContent = "";
      timers.announce = window.setTimeout(function () {
        status.textContent = message;
        timers.announce = null;
      }, 20);
    }

    function field(panel, name) { return panel.querySelector('[data-field="' + name + '"]'); }
    function output(panel, name) { return panel.querySelector('[data-output="' + name + '"]'); }

    function setText(panel, name, text) {
      var el = output(panel, name);
      if (el) el.textContent = text;
    }

    function setDisabled(button, disabled) {
      button.setAttribute("aria-disabled", disabled ? "true" : "false");
    }

    function isDisabled(button) {
      return button.getAttribute("aria-disabled") === "true";
    }

    // Stores the exact text a copy button will place on the clipboard.
    function setCopyValue(button, value) {
      var label = button.getAttribute("data-copy-label") || "value";
      if (value === null) {
        button.removeAttribute("data-value");
        button.setAttribute("aria-label", "Copy " + label + ", no value");
        setDisabled(button, true);
      } else {
        button.setAttribute("data-value", value);
        button.setAttribute("aria-label", "Copy " + label + ", " + value);
        setDisabled(button, false);
      }
    }

    function show(panel, name, text, copyValue) {
      setText(panel, name, text);
      var button = panel.querySelector('[data-copy="' + name + '"]');
      if (button) setCopyValue(button, copyValue === undefined ? null : copyValue);
    }

    function setResult(panel, result) {
      panel.setAttribute("data-result", result);
    }

    // ---- Select options (from the core's supported lists) -------------------

    function populateSelects() {
      Array.prototype.forEach.call(root.querySelectorAll("select[data-options]"), function (select) {
        var type = select.getAttribute("data-options");
        var options = [];
        if (type === "sample-rates") {
          Array.prototype.forEach.call(core.supportedSampleRates, function (rate) {
            options.push({ value: String(rate), text: grouped(rate, 0) + " Hz" });
          });
        } else if (type === "time-signatures") {
          Array.prototype.forEach.call(core.supportedTimeSignatures, function (signature) {
            var label = signatureLabel(signature[0], signature[1]);
            options.push({ value: label, text: label });
          });
        }
        select.textContent = "";
        options.forEach(function (option) {
          var el = document.createElement("option");
          el.value = option.value;
          el.textContent = option.text;
          select.appendChild(el);
        });
        resetSelect(select);
      });
    }

    function resetSelect(select) {
      var preferred = select.getAttribute("data-default");
      var match = Array.prototype.some.call(select.options, function (option) { return option.value === preferred; });
      select.value = match ? preferred : (select.options[0] ? select.options[0].value : "");
    }

    function readSignature(panel) {
      var parts = field(panel, "signature").value.split("/");
      var numerator = Number(parts[0]);
      var denominator = Number(parts[1]);
      return { numerator: numerator, denominator: denominator, label: signatureLabel(numerator, denominator) };
    }

    function readSampleRate(panel, name) {
      return Number(field(panel, name).value);
    }

    // ---- Note values table (rows from the core's supported divisions) -------

    function buildTable() {
      while (notesHead.children.length > 1) notesHead.removeChild(notesHead.lastElementChild);
      notesBody.textContent = "";

      Array.prototype.forEach.call(core.supportedVariants, function (variant) {
        var th = document.createElement("th");
        th.scope = "col";
        th.textContent = VARIANT_LABELS[variant.id] || variant.id;
        notesHead.appendChild(th);
      });

      Array.prototype.forEach.call(core.supportedNoteDivisions, function (note) {
        var row = document.createElement("tr");
        var heading = document.createElement("th");
        heading.scope = "row";
        heading.innerHTML = '<span class="ac-bpm-samples-table__note-name"></span><span class="ac-bpm-samples-table__note-fraction"></span>';
        heading.firstChild.textContent = note.name;
        heading.lastChild.textContent = note.division;
        row.appendChild(heading);

        Array.prototype.forEach.call(core.supportedVariants, function (variant) {
          var cell = document.createElement("td");
          var wrap = document.createElement("div");
          wrap.className = "ac-bpm-samples-cell";
          var description = (VARIANT_LABELS[variant.id] || variant.id).toLowerCase() + " " + note.division + " note";
          wrap.appendChild(cellButton(note.division, variant.id, "ms", description + " in milliseconds"));
          wrap.appendChild(cellButton(note.division, variant.id, "frames", description + " in samples"));
          cell.appendChild(wrap);
          row.appendChild(cell);
        });
        notesBody.appendChild(row);
      });
    }

    function cellButton(division, variantId, unit, label) {
      var button = document.createElement("button");
      button.type = "button";
      button.className = "ac-bpm-samples-cell__copy ac-bpm-samples-cell__copy--" + unit;
      button.setAttribute("data-copy", "cell");
      button.setAttribute("data-cell", division + "|" + variantId + "|" + unit);
      button.setAttribute("data-copy-label", label);
      button.innerHTML = '<span data-cell-value>—</span>' + (unit === "ms" ? ' <span class="ac-bpm-samples-cell__unit">ms</span>' : "");
      setCopyValue(button, null);
      return button;
    }

    function renderTable(table) {
      var byKey = {};
      if (table) {
        table.rows.forEach(function (row) {
          Object.keys(row.values).forEach(function (variantId) {
            byKey[row.division + "|" + variantId] = row.values[variantId];
          });
        });
      }
      Array.prototype.forEach.call(notesBody.querySelectorAll("[data-cell]"), function (button) {
        var parts = button.getAttribute("data-cell").split("|");
        var entry = byKey[parts[0] + "|" + parts[1]];
        var valueEl = button.querySelector("[data-cell-value]");
        if (!entry) {
          valueEl.textContent = "—";
          setCopyValue(button, null);
        } else if (parts[2] === "ms") {
          valueEl.textContent = grouped(entry.durationMilliseconds, DIGITS.ms);
          setCopyValue(button, plain(entry.durationMilliseconds, DIGITS.ms));
        } else {
          valueEl.textContent = grouped(entry.frameCount, 0);
          setCopyValue(button, String(entry.frameCount));
        }
      });
    }

    // ---- Field validation ----------------------------------------------------

    function setFieldError(panel, input, message, invalid) {
      var errorEl = panel.querySelector('[data-error-for="' + input.getAttribute("data-field") + '"]');
      if (errorEl) {
        errorEl.textContent = message || "";
        errorEl.hidden = !message;
      }
      if (invalid) input.setAttribute("aria-invalid", "true");
      else input.removeAttribute("aria-invalid");
    }

    // Returns { value, message, invalid }. While typing ("input"), an empty or
    // too-low tempo is not flagged yet; on "change" it is.
    function readField(input, eventType) {
      var kind = input.getAttribute("data-kind");
      var committed = eventType === "change";
      var validity = input.validity;
      var errorMessage = kind === "bpm" ? MESSAGES.bpmNumber : MESSAGES[kind];

      if (validity.badInput) return { value: null, message: errorMessage, invalid: true };
      if (input.value.trim() === "") {
        return { value: null, message: committed ? (kind === "bpm" ? MESSAGES.bpmEmpty : MESSAGES[kind]) : "", invalid: committed };
      }
      var value = input.valueAsNumber;
      if (!Number.isFinite(value)) return { value: null, message: errorMessage, invalid: true };

      if (kind === "bpm") {
        var check = core.validateBpm(value);
        if (!check.ok) {
          // Below-minimum values may still be mid-typing ("1" on the way to "120").
          if (value < 20 && value > 0 && !committed) return { value: null, message: "", invalid: false };
          return { value: null, message: MESSAGES.bpmRange, invalid: true };
        }
        return { value: check.value, message: "", invalid: false };
      }
      if (kind === "seconds") {
        if (value <= 0) return { value: null, message: MESSAGES.seconds, invalid: true };
        return { value: value, message: "", invalid: false };
      }
      // bars and frames: whole numbers inside the input's min/max.
      if (!Number.isInteger(value) || validity.rangeUnderflow || validity.rangeOverflow || validity.stepMismatch) {
        return { value: null, message: MESSAGES[kind], invalid: true };
      }
      return { value: value, message: "", invalid: false };
    }

    function updateField(panelName, input, eventType) {
      var result = readField(input, eventType);
      values[panelName][input.getAttribute("data-field")] = result.value;
      setFieldError(panels[panelName], input, result.message, result.invalid);
    }

    function readAllFields(panelName, eventType) {
      Array.prototype.forEach.call(panels[panelName].querySelectorAll("input[data-kind]"), function (input) {
        updateField(panelName, input, eventType);
      });
    }

    // ---- Workflow 1: Calculate loop -----------------------------------------

    var LOOP_OUTPUTS = ["frameCount", "durationSeconds", "durationMilliseconds", "barFrameCount", "barDurationMilliseconds", "quarterNoteDurationMilliseconds"];
    var METER_NOTE = "BPM means quarter notes per minute, as in most DAWs.";

    function clearLoop(summary) {
      var panel = panels.loop;
      LOOP_OUTPUTS.forEach(function (name) { show(panel, name, "—", null); });
      setText(panel, "summary", summary);
      renderTable(null);
      state.loopExport = null;
      setDisabled(copyAll, true);
      setResult(panel, "invalid");
    }

    function renderQuickBars() {
      Array.prototype.forEach.call(panels.loop.querySelectorAll("[data-quick-bars]"), function (button) {
        var pressed = Number(button.getAttribute("data-quick-bars")) === values.loop.bars;
        button.setAttribute("aria-pressed", pressed ? "true" : "false");
      });
    }

    function renderLoop() {
      var panel = panels.loop;
      var v = values.loop;
      var signature = readSignature(panel);
      var sampleRate = readSampleRate(panel, "sampleRate");
      renderQuickBars();

      if (v.bpm == null || v.bars == null) {
        clearLoop("Enter a tempo and a bar count to calculate.");
        return;
      }

      var loop = core.calculateLoop({
        bpm: v.bpm,
        sampleRate: sampleRate,
        numerator: signature.numerator,
        denominator: signature.denominator,
        bars: v.bars
      });
      var table = loop.ok ? core.noteSamplesTable({ bpm: v.bpm, sampleRate: sampleRate }) : loop;
      if (!loop.ok || !table.ok) {
        clearLoop((loop.ok ? table : loop).error.message);
        return;
      }

      show(panel, "frameCount", grouped(loop.frameCount, 0) + " samples", String(loop.frameCount));
      show(panel, "durationSeconds", grouped(loop.durationSeconds, DIGITS.seconds) + " s", plain(loop.durationSeconds, DIGITS.seconds));
      show(panel, "durationMilliseconds", grouped(loop.durationMilliseconds, DIGITS.ms) + " ms", plain(loop.durationMilliseconds, DIGITS.ms));
      show(panel, "barFrameCount", grouped(loop.barFrameCount, 0) + " samples", String(loop.barFrameCount));
      show(panel, "barDurationMilliseconds", grouped(loop.barDurationMilliseconds, DIGITS.ms) + " ms", plain(loop.barDurationMilliseconds, DIGITS.ms));
      show(panel, "quarterNoteDurationMilliseconds", grouped(loop.quarterNoteDurationMilliseconds, DIGITS.ms) + " ms", plain(loop.quarterNoteDurationMilliseconds, DIGITS.ms));

      setText(panel, "summary", plural(loop.bars, "bar", "bars") + " of " + signature.label + " at " + bpmText(loop.bpm) +
        " BPM and " + grouped(loop.sampleRate, 0) + " Hz (" + quarterNotesText(loop.quarterNotesTotal) + ").");

      var meter = METER_NOTE + " Each bar of " + signature.label + " lasts " + quarterNotesText(loop.quarterNotesPerBar) + ".";
      if (signature.denominator === 8) {
        meter += " In compound and odd meters the tempo still counts quarter notes, not the dotted-quarter or eighth-note pulse you may feel: " +
          signature.numerator + " eighth notes make " + quarterNotesText(loop.quarterNotesPerBar) + ".";
      }
      setText(panel, "meterNote", meter);

      renderTable(table);
      state.loopExport = { loop: loop, table: table, signature: signature };
      setDisabled(copyAll, false);
      setResult(panel, "ready");
    }

    function loopExportText() {
      var data = state.loopExport;
      var loop = data.loop;
      var lines = [
        "Audiocolour BPM to Samples & Loop Calculator",
        "Tempo\t" + bpmText(loop.bpm) + " BPM (quarter notes per minute)",
        "Sample rate\t" + loop.sampleRate + " Hz",
        "Time signature\t" + data.signature.label + " (" + quarterNotesText(loop.quarterNotesPerBar) + " per bar)",
        "Bars\t" + loop.bars,
        "",
        "Loop\tValue\tUnit",
        "Loop length\t" + loop.frameCount + "\tsamples",
        "Loop length\t" + plain(loop.durationSeconds, DIGITS.seconds) + "\ts",
        "Loop length\t" + plain(loop.durationMilliseconds, DIGITS.ms) + "\tms",
        "One bar\t" + loop.barFrameCount + "\tsamples",
        "One bar\t" + plain(loop.barDurationMilliseconds, DIGITS.ms) + "\tms",
        "Quarter note\t" + plain(loop.quarterNoteDurationMilliseconds, DIGITS.ms) + "\tms",
        ""
      ];
      var variants = Array.prototype.map.call(core.supportedVariants, function (variant) { return variant.id; });
      var header = ["Note"];
      variants.forEach(function (id) {
        var name = VARIANT_LABELS[id] || id;
        header.push(name + " (ms)", name + " (samples)");
      });
      lines.push(header.join("\t"));
      data.table.rows.forEach(function (row) {
        var cells = [row.division + " " + row.name];
        variants.forEach(function (id) {
          cells.push(plain(row.values[id].durationMilliseconds, DIGITS.ms), String(row.values[id].frameCount));
        });
        lines.push(cells.join("\t"));
      });
      lines.push("", "Sample counts are rounded to the nearest whole frame.");
      return lines.join("\n");
    }

    // ---- Workflow 2: Find BPM -------------------------------------------------

    function findMode() {
      var checked = panels.find.querySelector('[data-field="mode"]:checked');
      return checked ? checked.value : "duration";
    }

    function clearFind(summary, result) {
      var panel = panels.find;
      show(panel, "bpm", "—", null);
      setText(panel, "measurement", "—");
      setText(panel, "assumptions", "—");
      setText(panel, "summary", summary);
      readingsList.textContent = "";
      var note = output(panel, "interpretationNote");
      note.textContent = "";
      note.hidden = true;
      setResult(panel, result || "invalid");
    }

    function renderFind() {
      var panel = panels.find;
      var v = values.find;
      var mode = findMode();
      var signature = readSignature(panel);

      Array.prototype.forEach.call(panel.querySelectorAll("[data-mode-group]"), function (group) {
        group.hidden = group.getAttribute("data-mode-group") !== mode;
      });

      var hasMeasurement = mode === "duration" ? v.durationSeconds != null : v.frameCount != null;
      if (!hasMeasurement || v.bars == null) {
        clearFind("Enter a measured length and the number of bars it contains.");
        return;
      }

      var input = { numerator: signature.numerator, denominator: signature.denominator, bars: v.bars };
      var result;
      if (mode === "duration") {
        input.durationSeconds = v.durationSeconds;
        result = core.findBpmFromDuration(input);
      } else {
        input.frameCount = v.frameCount;
        input.sourceSampleRate = readSampleRate(panel, "sourceSampleRate");
        result = core.findBpmFromFrames(input);
      }

      var barsLabel = plural(v.bars, "bar", "bars") + " of " + signature.label;
      if (!result.ok) {
        clearFind(result.error.code === "bpm_out_of_range"
          ? "That length gives a tempo outside 20–300 BPM for " + barsLabel + ". Check the measurement, bar count and time signature."
          : result.error.message);
        return;
      }

      var seconds = grouped(result.durationSeconds, DIGITS.seconds) + " s";
      show(panel, "bpm", bpmText(result.bpm) + " BPM", bpmText(result.bpm));
      setText(panel, "measurement", mode === "duration"
        ? seconds + " (measured duration)"
        : grouped(result.frameCount, 0) + " frames at " + grouped(result.sourceSampleRate, 0) + " Hz = " + seconds);
      setText(panel, "assumptions", barsLabel + " (" + quarterNotesText(result.quarterNotesTotal) + ")");
      setText(panel, "summary", barsLabel + " lasting " + seconds + " is " + bpmText(result.bpm) + " BPM.");

      renderReadings(result, signature, seconds);
      setResult(panel, "ready");
    }

    function renderReadings(result, signature, seconds) {
      readingsList.textContent = "";
      var shown = {};
      result.interpretations.forEach(function (reading) {
        shown[reading.interpretation] = true;
        var item = document.createElement("li");
        item.className = "ac-bpm-samples-reading";
        var supported = reading.interpretation === "base" || reading.supported;
        item.setAttribute("data-supported", supported ? "true" : "false");

        var text = document.createElement("div");
        var name = document.createElement("span");
        name.className = "ac-bpm-samples-reading__name";
        name.textContent = READING_LABELS[reading.interpretation] || reading.interpretation;
        var bpm = document.createElement("span");
        bpm.className = "ac-bpm-samples-reading__bpm";
        bpm.textContent = bpmText(reading.bpm) + " BPM";
        var detail = document.createElement("span");
        detail.className = "ac-bpm-samples-reading__detail";
        var barsText = plural(reading.bars, "bar", "bars") + " of " + signature.label;
        if (reading.interpretation === "base") {
          detail.textContent = "The same " + seconds + " counted as " + barsText + ", as entered.";
        } else {
          detail.textContent = "The same " + seconds + " counted as " + barsText + " instead of " + grouped(result.bars, 0) + ". " +
            (reading.interpretation === "half-time" ? "Each bar is twice as long." : "Each bar is half as long.");
        }
        if (!supported) detail.textContent += " This tempo is outside the 20–300 BPM range the calculator supports.";
        text.appendChild(name);
        text.appendChild(bpm);
        text.appendChild(detail);
        item.appendChild(text);

        if (supported) {
          var button = document.createElement("button");
          button.type = "button";
          button.className = "ac-bpm-samples-copy";
          button.setAttribute("data-copy", "reading");
          button.setAttribute("data-copy-label", (READING_LABELS[reading.interpretation] || reading.interpretation).toLowerCase() + " tempo in BPM");
          button.innerHTML = '<span data-copy-text data-idle-text="Copy">Copy</span>';
          setCopyValue(button, bpmText(reading.bpm));
          item.appendChild(button);
        }
        readingsList.appendChild(item);
      });

      var missing = [];
      if (!shown["half-time"]) missing.push("Half-time isn't shown because " + plural(result.bars, "bar", "bars") + " can't be halved into a whole number of bars.");
      if (!shown["double-time"]) missing.push("Double-time isn't shown because the doubled bar count would be more than 999 bars.");
      var note = output(panels.find, "interpretationNote");
      note.textContent = missing.join(" ");
      note.hidden = missing.length === 0;
    }

    // ---- Workflow 3: Match tempo -----------------------------------------------

    var MATCH_COPY_OUTPUTS = ["durationPercentage", "playbackRatePercentage", "repitchSemitones"];

    function clearMatch(summary) {
      var panel = panels.match;
      ["sourceDuration", "targetDuration"].forEach(function (name) { setText(panel, name, "—"); });
      ["sourceTempo", "targetTempo"].forEach(function (name) { setText(panel, name, ""); });
      MATCH_COPY_OUTPUTS.forEach(function (name) { show(panel, name, "—", null); });
      setText(panel, "summary", summary);
      setResult(panel, "invalid");
    }

    function renderMatch() {
      var panel = panels.match;
      var v = values.match;
      var signature = readSignature(panel);

      if (v.sourceBpm == null || v.targetBpm == null || v.bars == null) {
        clearMatch("Enter a source tempo, a target tempo and a bar count to compare.");
        return;
      }

      var result = core.matchTempo({
        sourceBpm: v.sourceBpm,
        targetBpm: v.targetBpm,
        numerator: signature.numerator,
        denominator: signature.denominator,
        bars: v.bars
      });
      if (!result.ok) {
        clearMatch(result.error.message);
        return;
      }

      setText(panel, "sourceDuration", grouped(result.sourceDurationSeconds, DIGITS.seconds) + " s");
      setText(panel, "sourceTempo", "at " + bpmText(result.sourceBpm) + " BPM");
      setText(panel, "targetDuration", grouped(result.targetDurationSeconds, DIGITS.seconds) + " s");
      setText(panel, "targetTempo", "at " + bpmText(result.targetBpm) + " BPM");
      show(panel, "durationPercentage", grouped(result.durationPercentage, DIGITS.percent) + "%", plain(result.durationPercentage, DIGITS.percent));
      show(panel, "playbackRatePercentage", grouped(result.playbackRatePercentage, DIGITS.percent) + "%", plain(result.playbackRatePercentage, DIGITS.percent));
      show(panel, "repitchSemitones", signed(result.repitchSemitones, DIGITS.semitones), plain(result.repitchSemitones, DIGITS.semitones));

      var loopText = plural(result.bars, "bar", "bars") + " of " + signature.label;
      var summary;
      if (result.targetBpm === result.sourceBpm) {
        summary = "The tempos already match, so " + loopText + " needs no change.";
      } else if (result.targetBpm > result.sourceBpm) {
        summary = "Speeding " + loopText + " up from " + bpmText(result.sourceBpm) + " to " + bpmText(result.targetBpm) + " BPM makes it shorter.";
      } else {
        summary = "Slowing " + loopText + " down from " + bpmText(result.sourceBpm) + " to " + bpmText(result.targetBpm) + " BPM makes it longer.";
      }
      setText(panel, "summary", summary);
      setResult(panel, "ready");
    }

    var RENDERERS = { loop: renderLoop, find: renderFind, match: renderMatch };

    // ---- Tabs -----------------------------------------------------------------

    function selectTab(name, focus) {
      tabs.forEach(function (tab) {
        var selected = tab.getAttribute("data-ac-bpm-samples-tab") === name;
        tab.setAttribute("aria-selected", selected ? "true" : "false");
        tab.tabIndex = selected ? 0 : -1;
        panels[tab.getAttribute("data-ac-bpm-samples-tab")].hidden = !selected;
        if (selected && focus) tab.focus();
      });
    }

    on(tablist, "click", function (event) {
      var tab = event.target.closest("[data-ac-bpm-samples-tab]");
      if (tab && tablist.contains(tab)) selectTab(tab.getAttribute("data-ac-bpm-samples-tab"), false);
    });

    on(tablist, "keydown", function (event) {
      var current = tabs.indexOf(document.activeElement);
      if (current === -1) return;
      var next = null;
      if (event.key === "ArrowRight" || event.key === "Right") next = (current + 1) % tabs.length;
      else if (event.key === "ArrowLeft" || event.key === "Left") next = (current - 1 + tabs.length) % tabs.length;
      else if (event.key === "Home") next = 0;
      else if (event.key === "End") next = tabs.length - 1;
      if (next === null) return;
      event.preventDefault();
      selectTab(tabs[next].getAttribute("data-ac-bpm-samples-tab"), true);
    });

    // ---- Inputs ---------------------------------------------------------------

    function panelNameOf(el) {
      var panel = el.closest("[data-ac-bpm-samples-panel]");
      return panel && root.contains(panel) ? panel.getAttribute("data-ac-bpm-samples-panel") : null;
    }

    function handleInput(event) {
      var target = event.target;
      if (!target.matches("[data-field]")) return;
      var panelName = panelNameOf(target);
      if (!panelName) return;
      if (target.hasAttribute("data-kind")) updateField(panelName, target, event.type);
      RENDERERS[panelName]();
    }

    on(root, "input", handleInput);
    on(root, "change", handleInput);

    on(panels.loop, "click", function (event) {
      var quick = event.target.closest("[data-quick-bars]");
      if (!quick) return;
      var bars = field(panels.loop, "bars");
      bars.value = quick.getAttribute("data-quick-bars");
      updateField("loop", bars, "change");
      renderLoop();
    });

    // ---- Reset ----------------------------------------------------------------

    function resetPanel(panelName) {
      var panel = panels[panelName];
      Array.prototype.forEach.call(panel.querySelectorAll("input[data-kind]"), function (input) {
        input.value = input.defaultValue;
      });
      Array.prototype.forEach.call(panel.querySelectorAll("select[data-field]"), resetSelect);
      Array.prototype.forEach.call(panel.querySelectorAll('input[type="radio"][data-field]'), function (radio) {
        radio.checked = radio.defaultChecked;
      });
      state.copySequence += 1;
      clearCopyState();
      readAllFields(panelName, "initial");
      RENDERERS[panelName]();
      announce(PANEL_NAMES[panelName] + " reset to defaults.");
    }

    on(root, "click", function (event) {
      var reset = event.target.closest("[data-reset]");
      if (!reset || !root.contains(reset)) return;
      var panelName = panelNameOf(reset);
      if (panelName) resetPanel(panelName);
    });

    // ---- Copy -----------------------------------------------------------------

    function fallbackCopy(value) {
      var textarea = document.createElement("textarea");
      var previouslyFocused = document.activeElement;
      textarea.value = value;
      textarea.className = "ac-bpm-samples-copy-fallback";
      textarea.readOnly = true;
      var copied = false;
      try {
        root.appendChild(textarea);
        textarea.focus();
        textarea.setSelectionRange(0, textarea.value.length);
        copied = document.execCommand("copy");
      } catch (error) {
        copied = false;
      } finally {
        if (textarea.parentNode) textarea.parentNode.removeChild(textarea);
        if (previouslyFocused && typeof previouslyFocused.focus === "function") {
          try { previouslyFocused.focus(); } catch (error) { /* best effort */ }
        }
      }
      return copied;
    }

    function copyText(value) {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        return navigator.clipboard.writeText(value).then(
          function () { return true; },
          function () { return fallbackCopy(value); }
        );
      }
      return Promise.resolve(fallbackCopy(value));
    }

    // Copy feedback is shown on one button at a time across the instance.
    function setCopyState(button, copyState) {
      var label = button.querySelector("[data-copy-text]");
      if (copyState) {
        button.setAttribute("data-copy-state", copyState);
        if (label) label.textContent = copyState === "copied" ? "Copied" : "Copy failed";
      } else {
        button.removeAttribute("data-copy-state");
        if (label) label.textContent = label.getAttribute("data-idle-text");
      }
    }

    function clearCopyState() {
      clearTimer("copy");
      if (state.copyButton) setCopyState(state.copyButton, null);
      state.copyButton = null;
    }

    function showCopyFeedback(message, failed) {
      clearTimer("feedback");
      copyFeedback.textContent = message;
      copyFeedback.setAttribute("data-copy-state", failed ? "error" : "copied");
      copyFeedback.hidden = false;
      timers.feedback = window.setTimeout(function () {
        timers.feedback = null;
        copyFeedback.hidden = true;
        copyFeedback.textContent = "";
      }, failed ? COPY_FEEDBACK_MS * 3 : COPY_FEEDBACK_MS * 2);
    }

    function copyFrom(button, value, successMessage, failureMessage) {
      state.copySequence += 1;
      var thisCopy = state.copySequence;
      clearCopyState();

      copyText(value).then(function (copied) {
        if (thisCopy !== state.copySequence || controller.signal.aborted) return;
        state.copyButton = button;
        setCopyState(button, copied ? "copied" : "error");
        announce(copied ? successMessage : failureMessage);
        if (panels.loop.contains(button) && (button === copyAll || button.hasAttribute("data-cell"))) {
          showCopyFeedback(copied ? successMessage : failureMessage, !copied);
        }
        timers.copy = window.setTimeout(function () {
          timers.copy = null;
          if (thisCopy === state.copySequence) clearCopyState();
        }, COPY_FEEDBACK_MS);
      });
    }

    on(root, "click", function (event) {
      var button = event.target.closest("[data-copy]");
      if (!button || !root.contains(button) || isDisabled(button)) return;
      var value = button.getAttribute("data-value");
      if (!value) return;
      var label = button.getAttribute("data-copy-label") || "value";
      copyFrom(button, value, "Copied " + value + " (" + label + ").",
        "Couldn't copy " + value + ". Your browser blocked clipboard access; select the value and copy it manually.");
    });

    on(copyAll, "click", function () {
      if (isDisabled(copyAll) || !state.loopExport) return;
      copyFrom(copyAll, loopExportText(), "Copied all loop and note values as a table.",
        "Couldn't copy the table. Your browser blocked clipboard access.");
    });

    // ---- Start ----------------------------------------------------------------

    populateSelects();
    buildTable();
    PANELS.forEach(function (name) {
      readAllFields(name, "initial");
      RENDERERS[name]();
    });
    selectTab("loop", false);
    root.setAttribute("data-state", "ready");

    return {
      destroy: function () {
        controller.abort();
        Object.keys(timers).forEach(clearTimer);
      }
    };
  }

  function rootsIn(scope) {
    if (!scope || typeof scope.querySelectorAll !== "function") return [];
    var roots = Array.prototype.slice.call(scope.querySelectorAll("[data-ac-bpm-samples]"));
    if (scope.matches && scope.matches("[data-ac-bpm-samples]")) roots.unshift(scope);
    return roots;
  }

  function init(scope) {
    var core = window.AudiocolourBpmSamplesCore;
    rootsIn(scope || document).forEach(function (root) {
      if (instances.has(root)) return;
      var instance = core ? createInstance(root, core) : null;
      if (!instance) {
        var notice = root.querySelector("[data-ac-bpm-samples-unavailable]");
        if (notice) notice.hidden = false;
        root.setAttribute("data-state", "unavailable");
        return;
      }
      instances.set(root, instance);
    });
  }

  function destroy(scope) {
    rootsIn(scope || document).forEach(function (root) {
      var instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  window.AudiocolourBpmSamples = Object.freeze({ init: init, destroy: destroy });

  // Theme editor: sections are re-rendered in place.
  document.addEventListener("shopify:section:unload", function (event) { destroy(event.target); });
  document.addEventListener("shopify:section:load", function (event) { init(event.target); });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(document); });
  } else {
    init(document);
  }
}());
